import React from 'react';

import Image from 'components/uiLibrary/Image';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { SECTIONS, COMPONENTS } from 'components/Globals/Analytics/constants';
import LanguageSelector from 'components/Globals/LanguageSelector';
import { LP_PRO_TOOL_ITEM } from 'components/LandingPages/common/consts';

import { FACEBOOK, INSTAGRAM, LINKEDIN, OUTBOUND_LINK_TYPES, TP, TWITTER } from 'constants/index';

import { createDate } from 'utils/date';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import operaWhiteLogo from 'public/images/operabasewhite.svg';

import classes from './Footer.module.scss';

const socialLinks = [
  { link: FACEBOOK, type: 'facebook', title: 'Facebook' },
  { link: TWITTER, type: 'twitter', title: 'Twitter' },
  { link: INSTAGRAM, type: 'instagram', title: 'Instagram' },
  { link: LINKEDIN, type: 'linkedin', title: 'LinkedIn' },
];

const LINKS = {
  audience: {
    key: 'audience',
    title: `${TP}.FN_AUDIENCE`,
    items: [
      {
        path: ROUTE_RESERVED_KEYWORDS.intro,
        title: `${TP}.FN_INTRODUCTION_HISTORY`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.contact,
        title: `${TP}.FN_CONTACT_US`,
      },
    ],
  },
  explore: {
    key: 'explore',
    title: `${TP}.EXPLORE`,
    items: [
      {
        path: '/',
        title: `${TP}.m_HOME`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.productions,
        title: `${TP}.FN_PERFORMANCES`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS['watch-online'],
        title: `${TP}.ONLINE_VIDEOS`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.artists,
        title: `${TP}.m_ARTISTS`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.organisations,
        title: `${TP}.m_OPCOS`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.managers,
        title: `${TP}.AGENCY_MANAGER`,
      },
      {
        path: `${ROUTE_RESERVED_KEYWORDS.organisations}/festival`,
        title: `${TP}.m_FESTIVALS`,
      },
      {
        path: `${ROUTE_RESERVED_KEYWORDS.organisations}/venue`,
        title: `${TP}.VENUES_AND_MAP`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.works,
        title: `${TP}.FN_MUSICAL_WORKS_SECTION_NAME`,
      },
      {
        path: ROUTE_RESERVED_KEYWORDS.statistics,
        title: `${TP}.m_STATISTICS`,
      },
      {
        path: `${ROUTE_RESERVED_KEYWORDS.organisations}/competition`,
        title: `${TP}.m_COMPETITIONS`,
      },
    ],
  },
  operabaseFor: {
    key: 'operabaseFor',
    title: 'NUDGE_TITLE_MAIN',
    items: [
      { title: `${TP}.m_ARTISTS`, path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.artists}` },
      {
        title: `${TP}.FN_ARTS_ORGANISATIONS`,
        path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.companies}`,
      },
      { title: `${TP}.AGENTS`, path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.agencies}` },
      { title: `${TP}.ACADEMICS`, path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.academia}` },
      {
        title: `${TP}.JOURNALISTS_AND_MEDIA`,
        path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.journalist}`,
      },
    ],
  },
  proTools: {
    key: 'proTools',
    title: `${TP}.FN_PRO_TOOL`,
    items: [
      { title: `${TP}.FN_CASTINGTOOL`, path: ROUTE_RESERVED_KEYWORDS.casting, isPro: true },
      { title: `${TP}.FN_ARTISTSEARCH`, path: ROUTE_RESERVED_KEYWORDS.casting, isPro: true },
      {
        title: `${TP}.LP_ARTIST_PROFILE_MANAGEMENT`,
        path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.all}`,
        key: LP_PRO_TOOL_ITEM.AP_MANAGEMENT,
      },
      {
        title: `${TP}.LP_AGENCY_ROSTER_MANAGEMENT`,
        path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.all}`,
        key: LP_PRO_TOOL_ITEM.AGENCY_ROSTER_MNGMNT,
      },
      {
        title: `${TP}.FN_TAB_PRODUCTION_RENTALS`,
        path: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.all}`,
        key: LP_PRO_TOOL_ITEM.RENTALS,
      },
    ],
  },
};

const policyLinks = [
  {
    name: `${TP}.DATA_AND_PRIVACY`,
    path: ROUTE_RESERVED_KEYWORDS.privpol,
  },
  {
    name: `${TP}.TERMS`,
    path: ROUTE_RESERVED_KEYWORDS.termpol,
  },
  {
    name: `${TP}.COOKIE`,
    path: ROUTE_RESERVED_KEYWORDS.cookpol,
  },
  {
    name: `${TP}.FN_TERMPAY`,
    path: ROUTE_RESERVED_KEYWORDS['payment-terms'],
  },
  {
    name: 'Website Structure for AI and Bots',
    isText: true,
    path: 'https://help.operabase.com/knowledge/en/understanding-operabase-website-structure',
  },
];

const PolicyLinks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <ul className={classes.policyLinks}>
      {policyLinks.map((link, index) => (
        <li>
          <LinkButton
            key={`${link?.path}-${index}`}
            styles={{
              root: classes.policyLinks__link,
            }}
            {...(link.path.startsWith('http')
              ? {
                  href: link.path,
                  external: true,
                  externalLinkType: OUTBOUND_LINK_TYPES.WEBSITE,
                }
              : navigate.getLinkProps({ baseRoute: link.path }))}
            isLink
            variant="text"
            trackingData={{
              section: SECTIONS.FOOTER,
              component: COMPONENTS.POLICY_LINKS,
              subComponent: link.isText ? link.name : t(link.name),
              meta: {
                title: link.isText ? link.name : t(link.name),
              },
            }}
          >
            <Typography size="12" color="quaternary" weight="medium">
              {link.isText ? link.name : t(link.name)}
            </Typography>
          </LinkButton>
        </li>
      ))}
    </ul>
  );
};

const SocialLinks = () => (
  <div className={classes.socialLinks}>
    {socialLinks.map(({ link, type, title }) => (
      <LinkButton
        key={type}
        title={title}
        rel="noopener noreferrer"
        href={link}
        external
        externalLinkType={OUTBOUND_LINK_TYPES.SOCIAL_MEDIA}
        isLink
        variant="text"
        trackingData={{
          section: SECTIONS.FOOTER,
          component: COMPONENTS.SOCIAL_LINKS,
          subComponent: title,
          meta: {
            key: type,
            title,
          },
        }}
      >
        <SpriteIcon icon={type} size={32} hover />
      </LinkButton>
    ))}
  </div>
);

const ContactLinks = () => {
  const track = useTracking();
  const { t } = useTranslation('NS_APP_GLOBALS');
  return (
    <ul className={classes.menuLinks}>
      <li className={classes.menuLinks__section}>
        <SpriteIcon icon="chat" className={classes.contactIcon} />
        <Typography size="12" color="quaternary" weight="medium" className={classes.menuLinks__link}>
          {t(`${TP}.FN_LIVE_CHAT`)}
        </Typography>
      </li>
      <li className={classes.menuLinks__section}>
        <SpriteIcon icon="mail" className={classes.contactIcon} />
        <a
          className={classes.menuLinks__link}
          href="mailto:contact@operabase.com"
          onClick={() => {
            track.click({
              section: SECTIONS.FOOTER,
              component: COMPONENTS.CONTACT_VIA_EMAIL,
            });
          }}
        >
          <Typography size="12" color="quaternary" weight="medium">
            contact@operabase.com
          </Typography>
        </a>
      </li>
    </ul>
  );
};

const Copyright = () => (
  <Typography variant="span" color="quaternary" size={12} className={classes.copyright}>
    <>&#169; {createDate().year()} Arts Consolidated ApS. All rights reserved.</>
  </Typography>
);

const LinksSection = ({ links, isProTools }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <div>
      <Typography size="16" weight="bold" color="white" className={classes.sectionHeader}>
        {isProTools ? <SpriteIcon icon="apps" className={classes.proToolsIcon} /> : null}
        {t(links?.title)}
      </Typography>
      <ul className={classes.menuLinks}>
        {links.items.map((item, index) => (
          <li key={`${item?.path}-${index}`}>
            <Typography size="12" color="inherit">
              <LinkButton
                {...navigate.getLinkProps({ baseRoute: item?.key ? `${item.path}#${item.key}` : item.path })}
                isLink
                variant="text"
                styles={{
                  root: classes.menuLinks__link,
                }}
                trackingData={{
                  section: SECTIONS.FOOTER,
                  component: COMPONENTS.NAVIGATE_CTA,
                  subComponent: t(item?.title),
                  meta: {
                    section: LINKS.explore.key,
                    title: t(item?.title),
                  },
                }}
              >
                <Typography size="12" color="quaternary" weight="medium">
                  {t(item.title)}
                </Typography>
                <SpriteIcon icon="chevron_right" size={14} className={classes.chevronIcon} />
              </LinkButton>
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FooterMobile = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  return (
    <footer className={classes.footerMobile}>
      <div className={classes.footerMobile__logoSection}>
        <div className={classes.languageSelector}>
          <LanguageSelector type="footer" />
        </div>
        <Image
          src={operaWhiteLogo}
          width={190}
          height={22}
          alt="Operabase"
          title="Operabase"
          disableNextImage
          disableCloudinary
        />
        <Typography variant="p" className={classes.description} color="quaternary">
          {t(`${TP}.m_FOOTER_DESCRIPTION`)}
        </Typography>
        <SocialLinks />
      </div>
      <div className={classes.footerMobile__content}>
        <LinksSection links={LINKS.explore} />
        <LinksSection links={LINKS.operabaseFor} />
        <LinksSection links={LINKS.proTools} isProTools />
        <LinksSection links={LINKS.audience} />
      </div>
      <div className={classes.footerMobile__bottomSection}>
        <div>
          <Typography size="16" weight="bold" color="white" className={classes.sectionHeader}>
            {t(`${TP}.FN_GET_IN_TOUCH`)}
          </Typography>
          <ContactLinks />
          <PolicyLinks />
        </div>
      </div>
      <div className={classes.rights}>
        <Copyright />
      </div>
    </footer>
  );
};

const Footer = () => {
  const { isDesktop } = useDeviceTypeLayouts();
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!isDesktop) {
    return <FooterMobile />;
  }

  return (
    <footer className={classes.footer}>
      <div className={classes.footer__container}>
        <div className={classes.footer__content}>
          <div>
            <Image
              src={operaWhiteLogo}
              width={190}
              height={22}
              alt="Operabase"
              title="Operabase"
              disableNextImage
              disableCloudinary
            />
            <Typography variant="p" className={classes.description} color="quaternary">
              {t(`${TP}.m_FOOTER_DESCRIPTION`)}
            </Typography>
          </div>
          <LinksSection links={LINKS.explore} />
          <div className={classes.proTools}>
            <LinksSection links={LINKS.operabaseFor} />
            <LinksSection links={LINKS.proTools} isProTools />
          </div>
          <LinksSection links={LINKS.audience} />
          <div>
            <Typography size="16" weight="bold" color="white" className={classes.sectionHeader}>
              {t(`${TP}.FN_GET_IN_TOUCH`)}
            </Typography>
            <ContactLinks />
          </div>
        </div>
      </div>
      <div className={classes.footer__bottomContainer}>
        <div className={classes.footer__bottomSection}>
          <div className={classes.footer__bottomSection_left}>
            <Copyright />
            <PolicyLinks />
          </div>
          <div className={classes.footer__bottomSection_right}>
            <SocialLinks />
            <LanguageSelector placement="top" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
